// src/features/library/pages/LibraryPage.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePageScale } from "@/ui/usePageScale";
import { PageShell } from "@/ui/PageShell";
import { withBase } from "@/ui/Header";
import { libraryList, libraryDelete, libraryUpdate, bookStoreList, BookStore, LibraryStatus, api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { registerPager, unregisterPager } from "@/lib/pagerBridge";
import { LibrarySheet, LibrarySheetStatus } from "@/ui/LibrarySheet";
import { customConfirm } from "@/ui/confirm";
import { notify } from "@/ui/notify";
import { toCoverUrl } from "@/lib/coverUrl";
import { SPC_MODEL, getDeviceModel } from "@/lib/androidBridge";
import { COLOR, FW, BORDER } from "@/ui/tokens";

/* ───────────────── Config ───────────────── */
const BASE = {
    W: 1264,
    TAB_FZ: 35,
    MARGIN_X: 137,
    GRID_ROWS: 2,
    GRID_COLS: 3,
    COVER_W: 260,
    COVER_H: 370,
    TITLE_FZ: 32,
    TITLE_BOX_H: 88,
    GAP_COVER_TO_TITLE: 16,
    GRID_ROW_GAP: 35,
    // Figma(62:13) 기준 상단 3블록은 1264 좌표계에서 각각 100.
    // 내서재(62:449) / Tab(62:469) / 필터(62:1531)
    HEADER_H: 100,
    TAB_H: 100,
    FILTER_H: 100,
    // 필터 블록 끝 ~ 표지 상단 (Figma 435-403). 필터가 독립 블록이 되면서
    // 더 이상 FILTER_H를 빼지 않는다. 이 값이 커지면 2행 고정 그리드가 아래에서 잘린다.
    GRID_TOP_GAP: 32,
    SEARCH_PAGER_H: 100,
    // 구매처 필터 버튼 (62:1533) — 오른쪽 끝에서 36 떨어진다(그리드 여백과 다름)
    FILTER_BTN_W: 252,
    FILTER_BTN_H: 64,
    FILTER_BTN_RIGHT: 36,
    // 늘어나도 그리드 왼쪽 여백선은 넘지 않는다 (1264 - 36 - 137)
    FILTER_BTN_MAX_W: 1091,
    // 드롭다운 패널 (62:1539) — 버튼 오른쪽 끝에 맞춰 필터 블록 바로 아래로 펼친다
    DD_W: 320,
    DD_PAD_Y: 25,
    DD_ITEM_H: 86,
} as const;

const ICONS = {
    header: withBase("icons/library_on.svg"),
    first: withBase("icons/prev2.svg"),
    prev:  withBase("icons/prev1.svg"),
    next:  withBase("icons/next1.svg"),
    last:  withBase("icons/next2.svg"),
};

/* ──────────────── Scale ─────────────── */
function useScale() {
    // 이 페이지는 원래 r 최대를 1로 막고 있었으니 max: 1로 맞춰줌
    const { s, px, sf, sfpx } = usePageScale({ baseW: BASE.W, max: 1 });
    return { s, px, sf, sfpx };
}

/* ──────────────── Long Press ─────────────── */
function useLongPress(onLong:()=>void, opt:{ ms?:number; move?:number } = {}) {
    const { ms=450, move=8 } = opt;
    const timer = React.useRef<number|null>(null);
    const start = React.useRef<{x:number;y:number}|null>(null);
    const pressing = React.useRef(false);
    const clear = useCallback(()=>{ if(timer.current) window.clearTimeout(timer.current); timer.current=null; pressing.current=false; },[]);
    const down = useCallback((e:any)=>{
        if (e.button!==undefined && e.button!==0) return;
        const p = e.touches?.[0] ?? e;
        start.current = { x:p.clientX, y:p.clientY }; pressing.current=true;
        timer.current = window.setTimeout(()=>{ if(pressing.current) onLong(); clear(); }, ms);
    },[ms,onLong,clear]);
    const moveH = useCallback((e:any)=>{
        if(!pressing.current||!start.current) return;
        const p = e.touches?.[0] ?? e;
        const dx=Math.abs(p.clientX-start.current.x), dy=Math.abs(p.clientY-start.current.y);
        if(dx>move||dy>move) clear();
    },[move,clear]);
    const up = useCallback(()=>clear(),[clear]);
    const ctx = useCallback((e:any)=>e.preventDefault(),[]);
    return { onTouchStart:down,onTouchMove:moveH,onTouchEnd:up,onTouchCancel:up,onMouseDown:down,onMouseMove:moveH,onMouseUp:up,onMouseLeave:up,onContextMenu:ctx };
}

/* ──────────────── Types ─────────────── */
type Item = {
    id: number; status: Exclude<LibraryStatus,'ALL'>; created_at: string;
    store?: { id:number; code:string; name:string } | null;
    book: {
        id:number; title?:string; title_plain?:string; authors?:string; publisher?:string;
        isbn13?:string; isbn10?:string; cover_url?:string; pub_date?:string;
        synopsis?: string;
    };
};
type ListRes = {
    total: number; total_pages: number; has_prev: boolean; has_next: boolean;
    items: Item[];
    // 필터 드롭다운용 — 마스터 전체가 아니라 "내 서재에서 실제로 쓰인" 구매처만 온다.
    facets?: { stores: BookStore[]; has_unassigned: boolean };
};

type Dims = {
    cardW: number;
    coverH: number;
    titlePx: number;
    titleBoxH: number;
    gapCoverToTitle: number;
};

/* ──────────────── UI ─────────────── */
function Tabs({ value, onChange, px, s, sfpx }:{ value:LibraryStatus; onChange:(v:LibraryStatus)=>void; px:(n:number)=>string; s:(n:number)=>number; sfpx:(n:number)=>string }) {
    const Tab = ({ v, label }:{ v:LibraryStatus; label:string }) => {
        const active = value===v;
        return (
            <button
                onClick={()=>onChange(v)}
                style={{
                    width:"100%", height:px(BASE.TAB_H), fontSize:sfpx(BASE.TAB_FZ), fontWeight:FW.extraBold,
                    border:"none", background:"transparent", cursor:"pointer",
                    color:COLOR.text,
                    borderBottom: active ? `${s(4)}px solid #000` : `${s(2)}px solid #e5e7eb`,
                }}
            >{label}</button>
        );
    };
    return (
        <div style={{ width:px(BASE.W), margin:"0 auto", display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr" }}>
            <Tab v="ALL" label="전체" />
            <Tab v="READING" label="읽고 있는 책" />
            <Tab v="WISHLIST" label="읽고 싶은 책" />
            <Tab v="FINISHED" label="완독한 책" />
        </div>
    );
}

/* 구매처 필터 — 'ALL' | 'none'(미지정) | 구매처 id.
   Figma(62:1531) 시안대로 검정 알약 버튼 + 아래로 펼치는 패널.
   패널은 position:absolute라 펼쳐도 2행 고정 그리드의 세로 예산을 건드리지 않는다. */
type StoreFilterValue = 'ALL' | 'none' | number;

/** Figma 62:1535(more 2) 자리의 아래 방향 셰브론. 전용 에셋이 없어 인라인으로 그린다. */
function ChevronDown({ size, color }:{ size:number; color:string }) {
    return (
        <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" style={{ display:"block" }}>
            <path d="M13 19 L24 30 L35 19" fill="none" stroke={color} strokeWidth={4}
                  strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function StoreFilter({ value, stores, showUnassigned, onChange, px, s, sfpx }:{
    value: StoreFilterValue; stores: BookStore[]; showUnassigned: boolean;
    onChange:(v:StoreFilterValue)=>void;
    px:(n:number)=>string; s:(n:number)=>number; sfpx:(n:number)=>string;
}) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement|null>(null);

    // 바깥을 누르면 닫는다. 네이티브 select와 달리 스스로 닫히지 않는다.
    useEffect(() => {
        if (!open) return;
        const onDocDown = (e: MouseEvent | TouchEvent) => {
            if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", onDocDown);
        document.addEventListener("touchstart", onDocDown);
        return () => {
            document.removeEventListener("mousedown", onDocDown);
            document.removeEventListener("touchstart", onDocDown);
        };
    }, [open]);

    const options: { key:string; label:string; val:StoreFilterValue }[] = [
        { key:"ALL", label:"구매처 전체", val:"ALL" },
        ...stores.map(st => ({ key:String(st.id), label:st.name, val:st.id as StoreFilterValue })),
        ...(showUnassigned ? [{ key:"none", label:"미지정", val:"none" as StoreFilterValue }] : []),
    ];
    const current = options.find(o => String(o.val) === String(value)) ?? options[0];

    return (
        <div ref={wrapRef} style={{
            flexShrink:0, height: px(BASE.FILTER_H), position:"relative",
            width: px(BASE.W), margin:"0 auto",
        }}>
            {/* 버튼 — 블록 안에서 세로 가운데, 오른쪽 끝에서 FILTER_BTN_RIGHT 만큼 */}
            <button
                onClick={()=>setOpen(o=>!o)}
                onMouseDown={e=>e.preventDefault()}
                style={{
                    position:"absolute", right: px(BASE.FILTER_BTN_RIGHT),
                    top: px((BASE.FILTER_H - BASE.FILTER_BTN_H) / 2),
                    // 긴 구매처 이름에 맞춰 늘어난다. 시안 폭(252)은 최소값으로만 유지하고,
                    // 왼쪽은 그리드 여백(MARGIN_X)까지만 침범하도록 상한을 둔다.
                    minWidth: px(BASE.FILTER_BTN_W), maxWidth: px(BASE.FILTER_BTN_MAX_W),
                    height: px(BASE.FILTER_BTN_H),
                    display:"flex", alignItems:"center", justifyContent:"center", gap: px(12),
                    padding: `0 ${px(15)}`, borderRadius: 999, border:"none",
                    background: COLOR.text, color: COLOR.white,
                    fontSize: sfpx(30), fontWeight: FW.bold, cursor:"pointer",
                }}
            >
                <span style={{ overflow:"hidden", whiteSpace:"nowrap", textOverflow:"ellipsis" }}>{current.label}</span>
                <span style={{ flexShrink:0, display:"flex" }}>
                    <ChevronDown size={s(48)} color={COLOR.white} />
                </span>
            </button>

            {/* 펼침 패널 — 버튼 오른쪽 끝에 맞추고 필터 블록 바로 아래로 */}
            {open && (
                <div style={{
                    position:"absolute", right: px(BASE.FILTER_BTN_RIGHT), top: px(BASE.FILTER_H),
                    width: px(BASE.DD_W), padding: `${px(BASE.DD_PAD_Y)} 0`,
                    background: COLOR.white, border: `${s(4)}px solid ${COLOR.text}`,
                    borderRadius: px(16), zIndex: 20, boxSizing:"border-box",
                    maxHeight: px(BASE.DD_PAD_Y * 2 + BASE.DD_ITEM_H * 6), overflowY:"auto",
                }}>
                    {options.map(o => (
                        <button
                            key={o.key}
                            onClick={()=>{ onChange(o.val); setOpen(false); }}
                            onMouseDown={e=>e.preventDefault()}
                            style={{
                                width:"100%", height: px(BASE.DD_ITEM_H), border:"none",
                                background:"transparent", cursor:"pointer",
                                fontSize: sfpx(32), color: COLOR.text,
                                fontWeight: String(o.val) === String(value) ? FW.extraBold : FW.bold,
                            }}
                        >
                            {o.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function Pager({ page, totalPages, hasNext, onFirst, onPrev, onNext, onLast, px, sfpx }:{
    page:number; totalPages:number; hasNext:boolean; onFirst:()=>void; onPrev:()=>void; onNext:()=>void; onLast:()=>void; px:(n:number)=>string; sfpx:(n:number)=>string;
}) {
    return (
        <div style={{ width:"100%", height:"100%" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%" }}>
                <button disabled={!(page>1)} onClick={onFirst} aria-label="처음"
                        style={{ lineHeight:0, background:"transparent", marginRight:px(48), border:"none", opacity:page>1?1:0.4, cursor:"pointer" }}>
                    <img src={ICONS.first} alt="" style={{ width:px(48), height:px(48), display:"block" }} />
                </button>
                <button disabled={!(page>1)} onClick={onPrev} aria-label="이전"
                        style={{ lineHeight:0, background:"transparent", marginRight:px(46), border:"none", opacity:page>1?1:0.4, cursor:"pointer" }}>
                    <img src={ICONS.prev} alt="" style={{ width:px(48), height:px(48), display:"block" }} />
                </button>
                <span style={{ fontWeight:700, fontFeatureSettings:"'tnum' 1", fontSize:sfpx(34), minWidth:px(120), textAlign:"center", marginRight:px(46) }}>
          {Math.max(1,page)} / {Math.max(1,totalPages)}
        </span>
                <button disabled={!hasNext} onClick={onNext} aria-label="다음"
                        style={{ lineHeight:0, background:"transparent", marginRight:px(48), border:"none", opacity:hasNext?1:0.4, cursor:"pointer" }}>
                    <img src={ICONS.next} alt="" style={{ width:px(48), height:px(48), display:"block" }} />
                </button>
                <button disabled={!hasNext} onClick={onLast} aria-label="마지막"
                        style={{ lineHeight:0, background:"transparent", border:"none", opacity:hasNext?1:0.4, cursor:"pointer" }}>
                    <img src={ICONS.last} alt="" style={{ width:px(48), height:px(48), display:"block" }} />
                </button>
            </div>
        </div>
    );
}



/* 카드  */
function Card({
                  item,
                  onLong,
                  onOpen,
                  dims
              }: {
    item: Item;
    onLong: (it: Item) => void;
    onOpen: (it: Item) => void;
    dims: Dims;
}) {
    // 롱탭 후 발생할 수 있는 클릭 무시 플래그
    const longFired = React.useRef(false);

    const lp = useLongPress(() => {
        longFired.current = true;
        onLong(item);
        // 짧은 시간 동안 클릭 무시
        setTimeout(() => {
            longFired.current = false;
        }, 300);
    }, { ms: 450 });

    const title = item.book.title_plain || item.book.title || "";

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (longFired.current) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        onOpen(item);
    };

    // 키보드 접근성(엔터/스페이스)도 상세 열기
    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen(item);
        }
    };

    return (
        <button
            type="button"
            {...lp}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            aria-label={title}
            style={{
                width: dims.cardW,
                textAlign: "left",
                background: "transparent",
                border: "none",
                padding: 0,
                cursor: "pointer",
                WebkitTouchCallout: "none" as any,
                userSelect: "none" as any,
            }}
        >
            <div
                style={{
                    width: dims.cardW,
                    height: dims.coverH,
                    overflow: "hidden",
                    background: COLOR.bgLighter,
                    border: "none",
                    borderRadius: 0,
                }}
            >
                {item.book.cover_url ? (
                    <img
                        src={toCoverUrl(item.book.cover_url)}
                        alt={title}
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                ) : (
                    <div
                        style={{
                            width: "100%",
                            height: "100%",
                            display: "grid",
                            placeItems: "center",
                            color: "#9ca3af",
                            fontSize: 12,
                        }}
                    >
                        No Image
                    </div>
                )}
            </div>

            <div
                style={{
                    marginTop: dims.gapCoverToTitle,
                    height: dims.titleBoxH,
                    width: "100%",
                    overflow: "hidden",
                    paddingBottom: 2,
                }}
            >
                <div
                    style={{
                        fontWeight: FW.extraBold,
                        fontSize: dims.titlePx,
                        lineHeight: 1.22,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        color: COLOR.text,
                    }}
                    title={title}
                >
                    {title}
                </div>
            </div>
        </button>
    );
}

/* ──────────────── Page ─────────────── */
export default function LibraryPage() {
    const { s, px, sfpx } = useScale();

    const nav = useNavigate();
    const openDetail = useCallback(async (it: Item) => {
      const b = it.book;
      try {
      let detail: any = null;
      if (b.isbn13 || b.isbn10) {
            const isbn = b.isbn13 || b.isbn10!;
            const { data } = await api.get(`/books/isbn/${encodeURIComponent(isbn)}`);
            detail = data?.book || null;
          }
      if (!detail && b.id) {
             const { data } = await api.get(`/books/by-id/${b.id}`);
             detail = data?.book || null;
           }
       const merged = detail
         ? { ...detail, description: detail.description || detail.synopsis || '' }
             : { ...b,      description: b.synopsis || b.description || '' };

           const id =
             merged.isbn13 || merged.isbn10 ||
             (b.id ? `db-${b.id}` : `work-${encodeURIComponent((b.title_plain || b.title || '').slice(0,80))}`);

           nav(`/books/${id}`, { state: { book: merged, hideButtons: true, from: 'library' } });
      } catch {
        const fallback = { ...b, description: b.synopsis || b.description || '' };
        const id = b.isbn13 || b.isbn10 || (b.id ? `db-${b.id}` : `work-${encodeURIComponent((b.title_plain || b.title || '').slice(0,80))}`);
        nav(`/books/${id}`, { state: { book: fallback, hideButtons: true, from: 'library' } });
      }
    }, [nav]);
    // 탭/페이지
    const [tab, setTab] = useState<LibraryStatus>("ALL");
    const [page, setPage] = useState(1);

    // 데이터
    const pageSize = BASE.GRID_ROWS * BASE.GRID_COLS; // 6
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string|null>(null);
    const [list, setList] = useState<ListRes>({ total:0, total_pages:1, has_prev:false, has_next:false, items:[] });

    // 구매처 마스터 목록 (수정 시트에서 사용).
    // 필터(facets)와 달리 마스터 전체가 필요하다 — 아직 안 써본 구매처도 고를 수 있어야 한다.
    const [stores, setStores] = useState<BookStore[]>([]);
    const [storesLoading, setStoresLoading] = useState(false);
    const storesLoadedRef = useRef(false);
    const loadStores = useCallback(() => {
        if (storesLoadedRef.current) return;
        storesLoadedRef.current = true;
        setStoresLoading(true);
        bookStoreList()
            .then(r => setStores(r.stores || []))
            .catch(() => { storesLoadedRef.current = false; }) // 실패 시 다음 열기에서 재시도
            .finally(() => setStoresLoading(false));
    }, []);

    // 롱탭 선택 항목 — 롱탭하면 바로 "서재 정보 수정하기" 시트가 열린다.
    // 읽기 상태/구매처/삭제가 전부 이 시트 안에 있어서 중간 메뉴를 두지 않는다.
    const [selected, setSelected] = useState<Item|null>(null);
    const [sheetOpen, setSheetOpen] = useState(false);
    const openSheetFor = useCallback((it: Item) => { setSelected(it); setSheetOpen(true); loadStores(); }, [loadStores]);
    const closeSheet   = useCallback(()=> setSheetOpen(false), []);

    // 구매처 필터 — 목록 응답의 facets가 소스
    const [storeFilter, setStoreFilter] = useState<StoreFilterValue>('ALL');
    // 구매처 변경 후 facets까지 갱신하려면 서버 재조회가 필요하다 (낙관적 갱신으로는 못 맞춤)
    const [reloadKey, setReloadKey] = useState(0);

    // 탭/필터 바뀌면 페이지 1로
    useEffect(()=>{ setPage(1); }, [tab, storeFilter]);

    // 물리키 페이저 브릿지 등록
    useEffect(() => {
        registerPager({
            next: () => setPage(p => Math.min(list.total_pages, p + 1)),
            prev: () => setPage(p => Math.max(1, p - 1)),
            first: () => setPage(1),
            last: () => setPage(list.total_pages || 1),
        });
        return () => unregisterPager();
    }, [list.total_pages]);

    // 목록 로드
    useEffect(()=>{
        let aborted = false;
        (async()=>{
            setLoading(true); setErr(null);
            try {
                const data = await libraryList({ status: tab, store_id: storeFilter, page, size: pageSize, full: 1 });
                if (!aborted) setList(data);
            } catch (e:any) {
                if (!aborted) setErr(e?.message || "불러오기 실패");
            } finally {
                if (!aborted) setLoading(false);
            }
        })();
        return ()=>{ aborted = true; };
    }, [tab, storeFilter, page, pageSize, reloadKey]);

    // 필터 드롭다운 항목. 선택 중인 구매처가 facets에서 빠져도(그 구매처 마지막 책을 옮기거나 삭제)
    // 항목을 남긴다 — 없애면 select가 빈 값을 보여주고, 보던 필터가 말없이 전체로 바뀐 것처럼 보인다.
    const storeNamesRef = useRef<Map<number,string>>(new Map());
    useEffect(()=>{
        for (const st of list.facets?.stores ?? []) storeNamesRef.current.set(st.id, st.name);
    }, [list.facets]);

    const filterStores = useMemo(()=>{
        const fs = list.facets?.stores ?? [];
        if (typeof storeFilter === 'number' && !fs.some(st => st.id === storeFilter)) {
            const name = storeNamesRef.current.get(storeFilter);
            if (name) return [...fs, { id: storeFilter, code: '', name }];
        }
        return fs;
    }, [list.facets, storeFilter]);

    const showUnassigned = (list.facets?.has_unassigned ?? false) || storeFilter === 'none';

    // 스케일된 카드 치수 계산

    const deviceModel = getDeviceModel();
    const isSpc = deviceModel === SPC_MODEL;
    const innerW = useMemo(()=> s(BASE.W - BASE.MARGIN_X*2), [s]);

    const { dims, colGap } = useMemo(()=> {
        if (isSpc) {
            // Use exact same BASE constants as BookSearchPage so cards look identical
            const cg      = s(24);          // BookSearchPage BASE.GAP
            const sInnerW = s(1048);        // BookSearchPage BASE.SEARCH_W
            const cardW   = Math.floor((sInnerW - cg * (BASE.GRID_COLS - 1)) / BASE.GRID_COLS) - 5;
            const ratio   = cardW / s(205); // BookSearchPage BASE.CARD_W
            return {
                colGap: cg,
                dims: {
                    cardW,
                    coverH:          Math.round(s(295) * ratio), // BookSearchPage BASE.CARD_H
                    titlePx:         Math.round(s(32)  * ratio), // same TITLE_FZ
                    titleBoxH:       Math.round(s(130) * ratio), // BookSearchPage BASE.TITLE_BOX_H
                    gapCoverToTitle: Math.round(s(35)  * ratio), // BookSearchPage BASE.GAP_COVER_TO_TITLE
                } as Dims,
            };
        }
        const cardW = s(BASE.COVER_W);
        const used = BASE.GRID_COLS * cardW;
        return {
            colGap: Math.max(s(8), Math.round((innerW - used) / (BASE.GRID_COLS - 1))),
            dims: {
                cardW,
                coverH:          s(BASE.COVER_H),
                titlePx:         s(BASE.TITLE_FZ),
                titleBoxH:       s(BASE.TITLE_BOX_H),
                gapCoverToTitle: s(BASE.GAP_COVER_TO_TITLE),
            } as Dims,
        };
    }, [innerW, s, isSpc]);

    // 그리드 높이(2행 고정). 필터가 독립 블록(FILTER_H)이 되면서 GRID_TOP_GAP은
    // 필터 블록 끝에서 표지 상단까지의 여백만 뜻한다 — 더 빼지 않는다.
    const gridTopPad = BASE.GRID_TOP_GAP;
    const rowBlock = dims.coverH + dims.gapCoverToTitle + dims.titleBoxH;
    const gridH    = s(gridTopPad) + BASE.GRID_ROWS * rowBlock + s(BASE.GRID_ROW_GAP) * (BASE.GRID_ROWS - 1);

    // SPC: pager same height as BookSearchPage; others: keep existing
    const pagerH = isSpc ? s(140) : s(BASE.SEARCH_PAGER_H);

    // 시트에 넘길 구매처 목록. 마스터(GET /book-stores)는 활성 항목만 주는데
    // 서재 기록에는 비활성 구매처가 그대로 남아 있다(GET /library의 JOIN엔 is_active 조건이 없다).
    // 그대로 두면 자기가 고른 구매처가 시트 어디에도 안 보여서 값이 날아간 것처럼 읽힌다.
    const sheetStores = useMemo(()=>{
        const cur = selected?.store;
        if (!cur || stores.some(s => s.id === cur.id)) return stores;
        return [...stores, { ...cur, is_primary: true }];
    }, [stores, selected]);

    // 시트의 "수정" — 상태와 구매처를 한 번에 반영한다.
    // 낙관적 갱신 대신 재조회한다. 상태가 바뀌면 현재 탭에서 빠질 수 있고, 구매처를 처음
    // 지정하면 필터 드롭다운(facets)에도 새 항목이 생겨야 하는데 둘 다 서버만 알 수 있다.
    const submitEdit = useCallback(async (status: LibrarySheetStatus, storeId: number|null) => {
        const it = selected;
        if (!it) return;
        closeSheet();
        try {
            await libraryUpdate(it.id, { status, store_id: storeId });
            setReloadKey(k => k + 1);
        } catch {
            notify("수정에 실패했습니다.");
        }
    }, [selected, closeSheet]);

    // 시트의 "삭제하기" — 검정 "수정" 버튼 바로 옆이라 오탭 위험이 있어 한 번 확인받는다.
    const deleteSelected = useCallback(async () => {
        const it = selected;
        if (!it) return;
        const ok = await customConfirm("내 서재에서 삭제할까요?", "삭제");
        if (!ok) return;
        closeSheet();
        try {
            await libraryDelete(it.id);
            setList(l=>({ ...l, items: l.items.filter(x=>x.id!==it.id) }));
            setReloadKey(k=>k+1);
        } catch {
            notify("삭제에 실패했습니다.");
        }
    }, [selected, closeSheet]);

    const hasResults = list.items.length > 0;

    return (
        <PageShell title="내 서재" icon={ICONS.header} width={px(BASE.W)}
                   // PageShell은 headerHeight를 원시 px로 쓴다. 본문은 spx로 줄어드는데 헤더만
                   // 고정이면 기기 폭에 따라 어긋나므로 미리 스케일해서 넘긴다.
                   headerHeight={s(BASE.HEADER_H)}>
            <div style={{ gridArea:"body", display:"flex", flexDirection:"column", height:"100%" }}>
                <Tabs value={tab} onChange={setTab} px={px} s={s} sfpx={sfpx} />

                <StoreFilter value={storeFilter} stores={filterStores} showUnassigned={showUnassigned}
                             onChange={setStoreFilter} px={px} s={s} sfpx={sfpx} />

                {/* 그리드 — takes remaining space */}
                <div style={{ flex:1, minHeight:0, overflow:"hidden" }}>
                    <div style={{ width: px(BASE.W), margin:"0 auto", height:"100%" }}>
                        {!hasResults && !loading && (
                            <div style={{ paddingTop: px(24) }}>
                                <div style={{ marginLeft: px(BASE.MARGIN_X), marginRight: px(BASE.MARGIN_X), padding: px(24), color: "#666", fontSize: sfpx(28) }}>항목이 없습니다.</div>
                            </div>
                        )}
                        {err && <div style={{ marginLeft: px(BASE.MARGIN_X), color:"#dc2626", fontSize: sfpx(28), paddingTop: px(24) }}>{err}</div>}
                        <div style={{ marginLeft: px(BASE.MARGIN_X), marginRight: px(BASE.MARGIN_X), paddingTop: px(gridTopPad),
                            height: `${gridH}px`, overflow:"hidden", boxSizing:"border-box" }}>

                            <div style={{
                                display:"grid",
                                gridTemplateColumns:`repeat(${BASE.GRID_COLS}, minmax(0, ${dims.cardW}px))`,
                                justifyContent:"start",
                                columnGap: `${colGap}px`,
                                rowGap: px(BASE.GRID_ROW_GAP),
                                alignContent:"start",
                            }}>
                                {(loading && list.items.length===0 ? Array.from({length: BASE.GRID_COLS*BASE.GRID_ROWS}) : list.items).map((it:any, i:number)=>(
                                    loading && list.items.length===0 ? (
                                        <div key={i} style={{ width: dims.cardW }}>
                                            <div style={{ height: dims.coverH, background:COLOR.border }} />
                                            <div style={{ height: dims.titleBoxH, width:"100%", marginTop: dims.gapCoverToTitle, background:COLOR.border }} />
                                        </div>
                                    ) : (
                                        <Card key={it.id} item={it} onLong={openSheetFor} onOpen={openDetail} dims={dims} />
                                    )
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 페이저 — pinned to bottom, same height as BookSearchPage pager */}
                <div style={{ flexShrink:0, height:`${pagerH}px`, marginLeft: px(BASE.MARGIN_X), marginRight: px(BASE.MARGIN_X) }}>
                    <Pager
                        page={page}
                        totalPages={list.total_pages}
                        hasNext={list.has_next}
                        onFirst={()=>setPage(1)}
                        onPrev={()=>setPage(p=>Math.max(1,p-1))}
                        onNext={()=>setPage(p=>Math.min(list.total_pages,p+1))}
                        onLast={()=>setPage(list.total_pages || 1)}
                        px={px}
                        sfpx={sfpx}
                    />
                </div>
            </div>

            {/* 롱탭 → 서재 정보 수정 시트 (읽기 상태 + 구매처 + 삭제) */}
            <LibrarySheet
                mode="edit"
                open={sheetOpen}
                stores={sheetStores}
                loading={storesLoading}
                initialStatus={selected?.status ?? null}
                initialStoreId={selected?.store?.id ?? null}
                onClose={closeSheet}
                onSubmit={submitEdit}
                onDelete={deleteSelected}
            />
        </PageShell>
    );
}

